import { formatMoney } from '@lib/money'
import { getInitials, getShortName } from '@/utils/userNames'
import Modal from '@components/Modal'
import { GetPayUpType, GetUserType } from '@orbit/api-types'
import React from 'react'
import {
    Actions,
    Button,
    ConfirmText,
    Content,
    CrossUserWarning,
    SummaryAmount,
    SummaryArrow,
    SummaryAvatar,
    SummaryAvatars,
    SummaryCard,
    SummaryCurrencyCode,
    SummaryName,
} from './DeletePayUpConfirmModal.styles'

interface DeletePayUpConfirmModalProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    payUp: GetPayUpType | null
    members: GetUserType[]
    currentUserId: number
    showCurrencyCode: boolean
    isSubmitting?: boolean
}

const formatAmount = formatMoney

const SmallAvatar: React.FC<{ member: GetUserType | undefined }> = ({ member }) => (
    <SummaryAvatar>
        {member?.profilePicture ? (
            <img src={member.profilePicture} alt={getInitials(member)} />
        ) : (
            getInitials(member)
        )}
    </SummaryAvatar>
)

const DeletePayUpConfirmModal: React.FC<DeletePayUpConfirmModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    payUp,
    members,
    currentUserId,
    showCurrencyCode,
    isSubmitting = false,
}) => {
    if (!payUp) return null

    const fromMember = members.find(m => m.id === payUp.fromUserId)
    const toMember = members.find(m => m.id === payUp.toUserId)
    const isCurrentUserParticipant =
        payUp.fromUserId === currentUserId || payUp.toUserId === currentUserId
    const showCrossUserWarning = !isCurrentUserParticipant

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Delete payup?"
            size="small"
            backgroundColor="#fff8f6"
            closeButtonColor="#a03c04"
            closeOnBackdropClick={false}
        >
            <Content>
                <ConfirmText>
                    This will permanently delete the following payup. This action cannot be undone.
                </ConfirmText>

                <SummaryCard>
                    <SummaryAvatars>
                        <SmallAvatar member={fromMember} />
                        <SummaryName>{getShortName(fromMember)}</SummaryName>
                        <SummaryArrow>→</SummaryArrow>
                        <SmallAvatar member={toMember} />
                        <SummaryName>{getShortName(toMember)}</SummaryName>
                    </SummaryAvatars>
                    <SummaryAmount>
                        {formatAmount(payUp.amount, payUp.currencyCode)}
                        {showCurrencyCode && (
                            <SummaryCurrencyCode>
                                {payUp.currencyCode.toUpperCase()}
                            </SummaryCurrencyCode>
                        )}
                    </SummaryAmount>
                </SummaryCard>

                {showCrossUserWarning && (
                    <CrossUserWarning>
                        This payup is between <strong>{getShortName(fromMember)}</strong> and{' '}
                        <strong>{getShortName(toMember)}</strong> — deleting will affect their balances.
                    </CrossUserWarning>
                )}

                <Actions>
                    <Button type="button" $variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        $variant="danger"
                        onClick={onConfirm}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Deleting...' : 'Delete payup'}
                    </Button>
                </Actions>
            </Content>
        </Modal>
    )
}

export default DeletePayUpConfirmModal
